const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';

/**
 * Fixed-height, variable-width desktop filmstrip and one-at-a-time mobile gallery.
 * Desktop clones are visual copies only; every action resolves back to an original media index.
 */
class VelaProductCarousel extends HTMLElement {
  #mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  #resizeObserver;
  #scrollTimer;
  #clickResetTimer;
  #sequenceWidth = 0;
  #baseStart = 0;
  #activeIndex = 0;
  #drag = null;
  #touchGesture = null;
  #suppressClick = false;
  #initialized = false;
  #rebuildFrame = null;
  #pendingPreserveIndex = true;
  #pendingForceRebuild = false;
  #idleCallbackId = null;
  #idleTimeoutId = null;
  #loopEnabled = false;
  #loopReady = false;
  #lastViewportWidth = 0;
  #lastViewportHeight = 0;
  #lastGap = 0;
  #lastDesktopState = null;
  #suppressScroll = false;
  #pendingMediaId = null;
  #navigationVersion = 0;

  connectedCallback() {
    this.viewport = this.querySelector('[data-vela-viewport]');
    this.track = this.querySelector('[data-vela-track]');
    this.previousButton = this.querySelector('[data-vela-previous]');
    this.nextButton = this.querySelector('[data-vela-next]');
    this.thumbnailButtons = Array.from(this.querySelectorAll('[data-vela-thumbnail]'));

    if (!this.viewport || !this.track) return;

    this.previousButton?.addEventListener('click', this.#previous);
    this.nextButton?.addEventListener('click', this.#next);
    this.thumbnailButtons.forEach((button) => button.addEventListener('click', this.#selectThumbnail));
    this.viewport.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.viewport.addEventListener('wheel', this.#handleWheel, { passive: true });
    this.viewport.addEventListener('pointerdown', this.#handlePointerDown);
    this.viewport.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.viewport.addEventListener('touchend', this.#handleTouchEnd, { passive: false });
    this.viewport.addEventListener('touchcancel', this.#handleTouchCancel, { passive: true });
    this.addEventListener('click', this.#blockClickAfterDrag, true);
    this.addEventListener('click', this.#handleOriginalClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.#mediaQuery.addEventListener('change', this.#handleBreakpointChange);

    const originals = this.originals;
    const selectedIndex = originals.findIndex((item) => item.dataset.mediaId === this.dataset.initialMediaId);
    this.#activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
    this.#syncZoomAvailability();
    if (this.#mediaQuery.matches) {
      this.#hydrateAll();
    } else {
      this.prepareIndex(this.#activeIndex);
    }

    if (document.readyState === 'complete') {
      queueMicrotask(this.#handleWindowLoad);
    } else {
      window.addEventListener('load', this.#handleWindowLoad, { once: true });
    }

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleRebuild({ preserveIndex: this.#initialized }));
    this.#resizeObserver.observe(this.viewport);

    this.#scheduleRebuild({ preserveIndex: false });
  }

  disconnectedCallback() {
    clearTimeout(this.#scrollTimer);
    clearTimeout(this.#clickResetTimer);
    this.cancelPendingSelection();
    if (this.#rebuildFrame !== null) cancelAnimationFrame(this.#rebuildFrame);
    this.#rebuildFrame = null;
    this.#pendingPreserveIndex = true;
    this.#pendingForceRebuild = false;
    this.#cancelLoopSetup();
    this.#resizeObserver?.disconnect();
    this.previousButton?.removeEventListener('click', this.#previous);
    this.nextButton?.removeEventListener('click', this.#next);
    this.thumbnailButtons.forEach((button) => button.removeEventListener('click', this.#selectThumbnail));
    this.viewport?.removeEventListener('scroll', this.#handleScroll);
    this.viewport?.removeEventListener('wheel', this.#handleWheel);
    this.viewport?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.viewport?.removeEventListener('touchstart', this.#handleTouchStart);
    this.viewport?.removeEventListener('touchend', this.#handleTouchEnd);
    this.viewport?.removeEventListener('touchcancel', this.#handleTouchCancel);
    this.removeEventListener('click', this.#blockClickAfterDrag, true);
    this.removeEventListener('click', this.#handleOriginalClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#mediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('load', this.#handleWindowLoad);
  }

  get originals() {
    return Array.from(this.track?.querySelectorAll(':scope > [data-vela-original]') || []);
  }

  getItem(index) {
    return this.originals[index];
  }

  /** Used by variant requests to avoid overriding a more recent manual action. */
  get navigationVersion() {
    return this.#navigationVersion;
  }

  cancelPendingSelection() {
    this.#pendingMediaId = null;
  }

  #handleManualNavigation() {
    this.cancelPendingSelection();
    this.#navigationVersion += 1;
  }

  /** Returns the visible-gallery image for reuse by the zoom dialog. */
  getImage(index) {
    return this.getItem(index)?.querySelector('img.product-media__image');
  }

  /** Hydrates the requested image and its immediate mobile neighbours. */
  prepareIndex(index, options = {}) {
    const count = this.originals.length;
    if (!count) return;

    const normalizedIndex = this.#mod(Number(index), count);
    const indexes = new Set([normalizedIndex]);
    if (options.includeAdjacent !== false) {
      if (this.#mediaQuery.matches) {
        indexes.add(this.#mod(normalizedIndex - 1, count));
        indexes.add(this.#mod(normalizedIndex + 1, count));
      } else {
        if (normalizedIndex > 0) indexes.add(normalizedIndex - 1);
        if (normalizedIndex < count - 1) indexes.add(normalizedIndex + 1);
      }
    }

    indexes.forEach((candidateIndex) => {
      this.#hydrateIndex(candidateIndex, candidateIndex === normalizedIndex ? 'high' : 'low');
    });
  }

  selectMedia(mediaId, options = {}) {
    const index = this.originals.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index < 0) return false;
    // Prepare the loop before starting a variant animation. Without its boundary
    // buffer, late images cannot reach the left edge, and inserting clones later
    // changes the destination coordinates while the browser is still scrolling.
    if (this.#mediaQuery.matches) this.#activateLoopNow();
    // Keep the selected media through a real resize, without treating an
    // intermediate animation frame as a new selection.
    this.#pendingMediaId = String(mediaId);
    this.selectIndex(index, { ...options, preservePendingMedia: true });
    // Selecting the current image may produce no scroll event at all.
    if (this.#initialized && this.viewport.clientWidth
      && Math.abs(this.#getItemScrollLeft(this.getItem(index)) - this.viewport.scrollLeft) <= 1) {
      this.cancelPendingSelection();
    } else {
      // An immediately cancelled native scroll can emit no scroll event. Use
      // the same settling debounce as normal scrolling; movement resets it.
      this.#scheduleScrollSettled();
    }
    return true;
  }

  selectIndex(index, options = {}) {
    const originals = this.originals;
    if (!originals.length) return;
    if (!options.preservePendingMedia) this.#handleManualNavigation();

    const normalizedIndex = ((Number(index) % originals.length) + originals.length) % originals.length;
    const behavior = options.animate === false || window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    this.prepareIndex(normalizedIndex);
    this.#activeIndex = normalizedIndex;
    this.#updateThumbnails();
    // A loop rebuild may have suppressed its own scroll event in this frame.
    // The explicit navigation that follows must still be observed and settled.
    this.#suppressScroll = false;

    if (this.#mediaQuery.matches) {
      this.#normalizeLoopPosition();
      const target = this.originals[normalizedIndex];
      if (target) this.viewport.scrollTo({ left: this.#getItemScrollLeft(target), behavior });
    } else {
      const target = originals[normalizedIndex];
      if (target) this.viewport.scrollTo({ left: this.#getItemScrollLeft(target), behavior });
    }
  }

  #applyPendingSelection() {
    if (!this.#pendingMediaId || !this.#initialized || !this.viewport?.clientWidth || this.#drag || this.#touchGesture) return;

    const originals = this.originals;
    const index = originals.findIndex((item) => item.dataset.mediaId === this.#pendingMediaId);
    if (index < 0) {
      this.cancelPendingSelection();
      return;
    }

    this.#activeIndex = index;
    this.prepareIndex(index);
    this.#updateThumbnails();
    const targetLeft = this.#getItemScrollLeft(originals[index]);
    if (Math.abs(this.viewport.scrollLeft - targetLeft) > 1) {
      this.#setScrollLeftWithoutHandling(targetLeft);
    }

    // Geometry is reserved by CSS; unrelated page resources do not determine
    // whether a selection is finished. A hidden gallery may still need its loop.
    const layoutReady = !this.#mediaQuery.matches || originals.length < 2 || this.#loopReady;
    if (layoutReady && Math.abs(this.viewport.scrollLeft - targetLeft) <= 1) {
      this.cancelPendingSelection();
    }
  }

  #scheduleRebuild({ preserveIndex = true, force = false } = {}) {
    this.#pendingPreserveIndex = this.#rebuildFrame === null
      ? preserveIndex
      : this.#pendingPreserveIndex && preserveIndex;
    this.#pendingForceRebuild ||= force;
    if (this.#rebuildFrame !== null) return;

    this.#rebuildFrame = requestAnimationFrame(() => {
      const nextPreserveIndex = this.#pendingPreserveIndex;
      const nextForceRebuild = this.#pendingForceRebuild;
      this.#rebuildFrame = null;
      this.#pendingPreserveIndex = true;
      this.#pendingForceRebuild = false;
      this.#rebuild({ preserveIndex: nextPreserveIndex, force: nextForceRebuild });
    });
  }

  #rebuild({ preserveIndex, force = false }) {
    if (!this.isConnected || !this.viewport?.clientWidth) return;

    const isDesktop = this.#mediaQuery.matches;
    const viewportWidth = Math.round(this.viewport.clientWidth);
    const viewportHeight = Math.round(this.viewport.clientHeight);
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const wantsLoop = isDesktop && this.#loopEnabled;
    if (
      this.#initialized
      && preserveIndex
      && !force
      && viewportWidth === this.#lastViewportWidth
      && viewportHeight === this.#lastViewportHeight
      && gap === this.#lastGap
      && isDesktop === this.#lastDesktopState
      && wantsLoop === this.#loopReady
    ) return;

    const selectedMediaId = this.#pendingMediaId
      || (preserveIndex ? this.originals[this.#activeIndex]?.dataset.mediaId : this.dataset.initialMediaId);
    const previousLogicalOffset = !this.#pendingMediaId && preserveIndex && this.#sequenceWidth > 0
      ? this.#mod(this.viewport.scrollLeft - this.#baseStart, this.#sequenceWidth)
      : null;
    this.#removeClones();

    const originals = this.originals;
    if (!originals.length) return;

    const selectedIndex = originals.findIndex((item) => item.dataset.mediaId === selectedMediaId);
    this.#activeIndex = selectedIndex >= 0 ? selectedIndex : Math.min(this.#activeIndex, originals.length - 1);
    if (isDesktop) {
      this.#hydrateAll();
    } else {
      this.prepareIndex(this.#activeIndex);
    }

    if (isDesktop && originals.length > 1) {
      // One repeating sequence includes the gap from its last image to the
      // first image of the following sequence, including the boundary clones.
      this.#sequenceWidth = originals.reduce((total, item) => total + item.getBoundingClientRect().width + gap, 0);
      if (this.#sequenceWidth > 0) {
        this.#baseStart = this.#getItemScrollLeft(originals[0]);
        if (wantsLoop) {
          this.#buildBoundaryClones(originals);
          this.#baseStart = this.#getItemScrollLeft(this.originals[0]);
          this.#loopReady = true;
        }

        const targetLeft = previousLogicalOffset === null
          ? this.#getItemScrollLeft(this.originals[this.#activeIndex])
          : this.#baseStart + Math.min(previousLogicalOffset, Math.max(0, this.#sequenceWidth - 1));
        this.#setScrollLeftWithoutHandling(targetLeft);
      }
    } else {
      this.#sequenceWidth = 0;
      this.#baseStart = 0;
      this.#setScrollLeftWithoutHandling(this.#getItemScrollLeft(originals[this.#activeIndex]));
    }

    this.#lastViewportWidth = viewportWidth;
    this.#lastViewportHeight = viewportHeight;
    this.#lastGap = gap;
    this.#lastDesktopState = isDesktop;
    this.#initialized = true;
    this.#updateThumbnails();
    this.#applyPendingSelection();
  }

  #buildBoundaryClones(originals) {
    const targetWidth = this.viewport.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const beforeItems = [];
    const after = document.createDocumentFragment();
    let beforeWidth = 0;
    let afterWidth = 0;
    let step = 0;
    const maxClonesPerSide = 200;

    while (beforeWidth < targetWidth && step < maxClonesPerSide) {
      const source = originals[this.#mod(originals.length - 1 - step, originals.length)];
      beforeItems.unshift(this.#makeClone(source));
      beforeWidth += source.getBoundingClientRect().width + gap;
      step += 1;
    }

    step = 0;
    while (afterWidth < targetWidth && step < maxClonesPerSide) {
      const source = originals[step % originals.length];
      after.append(this.#makeClone(source));
      afterWidth += source.getBoundingClientRect().width + gap;
      step += 1;
    }

    const before = document.createDocumentFragment();
    beforeItems.forEach((item) => before.append(item));
    this.track.prepend(before);
    this.track.append(after);
  }

  #setScrollLeftWithoutHandling(left) {
    this.#suppressScroll = true;
    this.viewport.scrollLeft = left;
    requestAnimationFrame(() => { this.#suppressScroll = false; });
  }

  #getItemScrollLeft(item) {
    if (!item) return 0;
    return item.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left + this.viewport.scrollLeft;
  }

  #makeClone(item) {
    const clone = item.cloneNode(true);
    clone.removeAttribute('data-vela-original');
    clone.dataset.velaClone = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('[tabindex], button, a, input, select, textarea').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    clone.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.fetchPriority = 'low';
      this.#revealDeferredImageWhenReady(image);
    });

    // Loop copies only need the poster. This prevents duplicated videos/models
    // from initializing while keeping the strip visually continuous.
    clone.querySelectorAll('.product-media > :not(.product-media__image)').forEach((element) => element.remove());
    return clone;
  }

  #hydrateIndex(index, priority = 'low') {
    if (!this.track) return;

    this.track
      .querySelectorAll(`[data-media-index="${index}"] img[data-vela-deferred-image]`)
      .forEach((image) => {
        if (!(image instanceof HTMLImageElement)) return;

        const src = image.dataset.src;
        const srcset = image.dataset.srcset;
        image.loading = 'eager';
        image.fetchPriority = priority;
        if (srcset) image.srcset = srcset;
        if (src) image.src = src;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
        this.#revealDeferredImageWhenReady(image);
      });
  }

  #revealDeferredImageWhenReady(image) {
    if (!image.hasAttribute('data-vela-deferred-image')) return;
    const reveal = () => image.removeAttribute('data-vela-deferred-image');
    if (image.complete && image.naturalWidth > 0) {
      reveal();
    } else {
      image.addEventListener('load', reveal, { once: true });
    }
  }

  #hydrateAll() {
    this.originals.forEach((item, index) => {
      const mediaIndex = Number(item.dataset.mediaIndex);
      if (!Number.isInteger(mediaIndex)) return;
      this.#hydrateIndex(mediaIndex, index === this.#activeIndex ? 'high' : 'low');
    });
  }

  #removeClones() {
    this.track?.querySelectorAll(':scope > [data-vela-clone]').forEach((clone) => clone.remove());
    this.#loopReady = false;
  }

  #handleBreakpointChange = () => {
    this.#cancelLoopSetup();
    this.#loopEnabled = false;
    this.#syncZoomAvailability();

    if (this.#mediaQuery.matches) {
      this.#hydrateAll();
      if (document.readyState === 'complete') this.#scheduleLoopSetup();
    }

    this.#scheduleRebuild({ preserveIndex: true, force: true });
  };

  #handleWindowLoad = () => {
    this.#hydrateAll();
    if (this.#mediaQuery.matches) this.#scheduleLoopSetup();
  };

  #scheduleLoopSetup() {
    if (this.#loopEnabled || this.#idleCallbackId !== null || this.#idleTimeoutId !== null) return;

    const activate = () => {
      this.#idleCallbackId = null;
      this.#idleTimeoutId = null;
      if (!this.isConnected || !this.#mediaQuery.matches) return;
      this.#loopEnabled = true;
      this.#scheduleRebuild({ preserveIndex: true, force: true });
    };

    if ('requestIdleCallback' in window) {
      this.#idleCallbackId = window.requestIdleCallback(activate, { timeout: 2000 });
    } else {
      this.#idleTimeoutId = window.setTimeout(activate, 0);
    }
  }

  #cancelLoopSetup() {
    if (this.#idleCallbackId !== null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.#idleCallbackId);
    }
    if (this.#idleTimeoutId !== null) window.clearTimeout(this.#idleTimeoutId);
    this.#idleCallbackId = null;
    this.#idleTimeoutId = null;
  }

  #activateLoopNow() {
    if (!this.#mediaQuery.matches || this.#loopReady) return;
    this.#cancelLoopSetup();
    // This synchronous rebuild includes any queued initial/resize work. Do not
    // let that older frame run afterward and interrupt the new animation.
    if (this.#rebuildFrame !== null) cancelAnimationFrame(this.#rebuildFrame);
    this.#rebuildFrame = null;
    this.#pendingPreserveIndex = true;
    this.#pendingForceRebuild = false;
    this.#loopEnabled = true;
    this.#rebuild({ preserveIndex: true, force: true });
  }

  #handleScroll = () => {
    if (!this.#initialized || this.#suppressScroll) return;
    this.#updateActiveFromScroll();
    this.#scheduleScrollSettled();
  };

  #scheduleScrollSettled() {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => {
      if (this.#pendingMediaId) {
        this.#applyPendingSelection();
        return;
      }
      if (this.#mediaQuery.matches && this.#loopReady && !this.#drag) {
        this.#normalizeLoopPosition();
      } else if (!this.#mediaQuery.matches && !this.#touchGesture) {
        this.#settleMobilePosition();
      }
      this.#updateActiveFromScroll();
    }, 180);
  }

  #updateActiveFromScroll() {
    const originals = this.originals;
    if (!originals.length) return;

    if (!this.#mediaQuery.matches) {
      this.#activeIndex = this.#getNearestMobileIndex();
      this.prepareIndex(this.#activeIndex);
      this.#updateThumbnails();
      return;
    }

    if (!this.#sequenceWidth) return;

    if (!this.#loopReady) {
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      originals.forEach((item, index) => {
        const distance = Math.abs(this.#getItemScrollLeft(item) - this.viewport.scrollLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      this.#activeIndex = nearestIndex;
      this.prepareIndex(this.#activeIndex);
      return;
    }

    const logicalLeft = this.#mod(this.viewport.scrollLeft - this.#baseStart, this.#sequenceWidth);
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    originals.forEach((item, index) => {
      const itemLeft = this.#getItemScrollLeft(item) - this.#baseStart;
      const direct = Math.abs(itemLeft - logicalLeft);
      const wrapped = Math.min(direct, this.#sequenceWidth - direct);
      if (wrapped < nearestDistance) {
        nearestDistance = wrapped;
        nearestIndex = index;
      }
    });

    this.#activeIndex = nearestIndex;
    this.prepareIndex(this.#activeIndex);
  }

  #normalizeLoopPosition() {
    if (!this.#loopReady || !this.#sequenceWidth) return;
    const normalized = this.#baseStart + this.#mod(this.viewport.scrollLeft - this.#baseStart, this.#sequenceWidth);
    if (Math.abs(normalized - this.viewport.scrollLeft) > 1) {
      const previousBehavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = normalized;
      this.viewport.style.scrollBehavior = previousBehavior;
    }
  }

  #previous = (event) => {
    event.preventDefault();
    this.#moveToAdjacentBoundary(-1);
  };

  #next = (event) => {
    event.preventDefault();
    this.#moveToAdjacentBoundary(1);
  };

  #moveToAdjacentBoundary(direction) {
    const originals = this.originals;
    if (!originals.length) return;
    this.#handleManualNavigation();

    this.prepareIndex(this.#activeIndex + direction);

    if (!this.#mediaQuery.matches) {
      this.selectIndex(this.#activeIndex + direction);
      return;
    }

    if (!this.#loopReady) {
      const adjacentIndex = this.#activeIndex + direction;
      if (adjacentIndex >= 0 && adjacentIndex < originals.length) {
        this.selectIndex(adjacentIndex);
        return;
      }
      this.#activateLoopNow();
    }

    const items = Array.from(this.track.children);
    const current = this.viewport.scrollLeft;
    const epsilon = 3;
    let target;

    if (direction > 0) {
      target = items.find((item) => this.#getItemScrollLeft(item) > current + epsilon);
    } else {
      target = [...items].reverse().find((item) => this.#getItemScrollLeft(item) < current - epsilon);
    }

    if (!target) {
      this.#normalizeLoopPosition();
      requestAnimationFrame(() => this.#moveToAdjacentBoundary(direction));
      return;
    }

    this.viewport.scrollTo({
      left: this.#getItemScrollLeft(target),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #selectThumbnail = (event) => {
    const index = Number(event.currentTarget.dataset.velaThumbnail);
    this.selectIndex(index);
  };

  #updateThumbnails() {
    this.thumbnailButtons.forEach((button, index) => {
      const active = index === this.#activeIndex;
      const wasActive = button.getAttribute('aria-current') === 'true';
      button.setAttribute('aria-current', active ? 'true' : 'false');
      if (active && !wasActive) button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  #handlePointerDown = (event) => {
    if (!this.#mediaQuery.matches || event.pointerType !== 'mouse' || event.button !== 0 || this.originals.length <= 1) return;
    if (event.target instanceof Element && event.target.closest('button, a, video, model-viewer, input, select')) return;
    this.#handleManualNavigation();

    // A deliberate drag can reach either edge before the idle task runs.
    // Build the small boundary buffer now so the gesture remains continuous.
    this.#activateLoopNow();

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.viewport.scrollLeft,
      moved: false,
    };
    this.viewport.addEventListener('pointermove', this.#handlePointerMove);
    this.viewport.addEventListener('pointerup', this.#handlePointerUp, { once: true });
    this.viewport.addEventListener('pointercancel', this.#handlePointerUp, { once: true });
  };

  #handlePointerMove = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;
    const delta = event.clientX - this.#drag.startX;
    if (!this.#drag.moved && Math.abs(delta) <= 5) return;

    if (!this.#drag.moved) {
      this.#drag.moved = true;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add('is-dragging');
    }

    event.preventDefault();
    this.viewport.scrollLeft = this.#drag.startScrollLeft - delta;
  };

  #handlePointerUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;
    const moved = this.#drag.moved;
    this.viewport.removeEventListener('pointermove', this.#handlePointerMove);
    this.viewport.removeEventListener('pointerup', this.#handlePointerUp);
    this.viewport.removeEventListener('pointercancel', this.#handlePointerUp);
    this.viewport.classList.remove('is-dragging');
    if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
    this.#drag = null;
    this.#updateActiveFromScroll();

    if (moved) {
      this.#suppressClick = true;
      requestAnimationFrame(() => requestAnimationFrame(() => { this.#suppressClick = false; }));
    }
  };

  #handleTouchStart = (event) => {
    if (this.#mediaQuery.matches || event.touches.length !== 1 || this.originals.length <= 1) return;
    const touch = event.touches[0];
    if (!touch) return;
    this.#handleManualNavigation();

    this.#touchGesture = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: performance.now(),
    };
  };

  #handleTouchEnd = (event) => {
    const gesture = this.#touchGesture;
    this.#touchGesture = null;
    if (!gesture || this.#mediaQuery.matches) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = gesture.startX - touch.clientX;
    const deltaY = gesture.startY - touch.clientY;
    const distance = Math.abs(deltaX);
    const duration = Math.max(1, performance.now() - gesture.startTime);
    const velocity = distance / duration;
    const horizontalGesture = distance > Math.abs(deltaY) * 1.2;
    const shouldChange = horizontalGesture && (distance >= 40 || (distance >= 12 && velocity >= 0.35));

    if (!shouldChange) return;

    // A swipe must not also open the zoom dialog when the synthetic click
    // follows touchend. Reset after the click window has passed.
    this.#suppressClick = true;
    clearTimeout(this.#clickResetTimer);
    this.#clickResetTimer = setTimeout(() => { this.#suppressClick = false; }, 350);

    // Do not pick a destination from the gesture's starting slide here.
    // Native momentum and scroll snap are still moving after touchend; the
    // debounced scroll handler settles against the position where they stop.
  };

  #handleTouchCancel = () => {
    this.#touchGesture = null;
  };

  #handleWheel = (event) => {
    if (event.deltaX || (event.shiftKey && event.deltaY)) this.#handleManualNavigation();
  };

  #getNearestMobileIndex() {
    const originals = this.originals;
    if (!originals.length) return 0;

    let nearestIndex = 0;
    let nearestDistance = Infinity;
    originals.forEach((item, index) => {
      const distance = Math.abs(this.#getItemScrollLeft(item) - this.viewport.scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    return nearestIndex;
  }

  #settleMobilePosition = () => {
    if (this.#mediaQuery.matches || this.#touchGesture) return;

    const originals = this.originals;
    if (!originals.length) return;
    const index = this.#getNearestMobileIndex();
    const target = originals[Math.max(0, Math.min(originals.length - 1, index))];
    if (!target) return;

    const targetLeft = this.#getItemScrollLeft(target);
    if (Math.abs(targetLeft - this.viewport.scrollLeft) > 1) {
      this.viewport.scrollTo({
        left: targetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }
  };

  #handleOriginalClick = async (event) => {
    if (this.#suppressClick || !this.#mediaQuery.matches) return;
    const target = event.target;
    const item = target instanceof Element
      ? target.closest('.vela-product-carousel__item--zoomable[data-media-index]')
      : null;
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;

    this.#openZoomItem(item, event);
  };

  #openZoomItem = async (item, event) => {
    const index = Number(item.dataset.mediaIndex);
    const mediaGallery = this.closest('media-gallery');
    if (!Number.isInteger(index) || !mediaGallery) return;
    this.#handleManualNavigation();

    // Keep a very early mobile tap from being lost while media-gallery.js is
    // still finishing its module upgrade on the live storefront.
    event.preventDefault();
    if (typeof mediaGallery.zoom !== 'function') {
      await customElements.whenDefined('media-gallery');
      customElements.upgrade(mediaGallery);
    }
    if (typeof mediaGallery.zoom !== 'function') return;

    mediaGallery.zoom(index, event);
  };

  #blockClickAfterDrag = (event) => {
    if (!this.#suppressClick) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#moveToAdjacentBoundary(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#moveToAdjacentBoundary(1);
    } else if (
      this.#mediaQuery.matches
      && (event.key === 'Enter' || event.key === ' ')
      && event.target instanceof Element
    ) {
      const zoomableItem = event.target.closest('[data-vela-original].vela-product-carousel__item--zoomable');
      if (zoomableItem) {
        event.preventDefault();
        zoomableItem.click();
      }
    }
  };

  #syncZoomAvailability() {
    const enabled = this.#mediaQuery.matches;
    this.originals
      .filter((item) => item.classList.contains('vela-product-carousel__item--zoomable'))
      .forEach((item) => {
        if (enabled) {
          item.setAttribute('role', 'button');
          item.setAttribute('tabindex', '0');
          if (item.dataset.velaZoomLabel) item.setAttribute('aria-label', item.dataset.velaZoomLabel);
          return;
        }

        const label = item.getAttribute('aria-label');
        if (label) item.dataset.velaZoomLabel = label;
        item.removeAttribute('role');
        item.removeAttribute('tabindex');
        item.removeAttribute('aria-label');
      });
  }

  #mod(value, divisor) {
    return ((value % divisor) + divisor) % divisor;
  }
}

if (!customElements.get('vela-product-carousel')) {
  customElements.define('vela-product-carousel', VelaProductCarousel);
}
